/**
 * Root Vuex store getters.
 *
 * @module store/getters
 * @description Provides computed properties for accessing and deriving state values from the root store.
 * Includes getters for user profile, configuration flags, content nodes, and trending topics.
 */
import { RootState } from "./types";
import _ from "lodash";
import { OrderUpdate, TrendingType } from "@/helperMethods/trendingTopics/type";

/**
 * Retrieves the agent's SOE ID from their Auth0 profile.
 *
 * @param {RootState} state - Root state
 * @returns {string} Agent's SOE ID or empty string
 */
export function getAgentSOEId(state: RootState): string {
  const agentSoeId = _.get(state, "profile.app_metadata.SOE_ID", "");
  return agentSoeId;
}

/**
 * Retrieves the agent's name from their Auth0 profile.
 *
 * @param {RootState} state - Root state
 * @returns {string} Agent's name or empty string
 */
export function getAgentName(state: RootState): string {
  const agentName = _.get(state, "profile.name", "");
  return agentName;
}

/**
 * Checks if advanced mode is enabled.
 *
 * @param {RootState} state - Root state
 * @returns {boolean} True if advanced mode enabled
 */
export function isShowAdvanced(state: RootState): boolean {
  return _.get(state, "showAdvanced", false);
}

/**
 * Checks if the current account is a Cyngus account.
 *
 * @param {RootState} state - Root state
 * @returns {boolean} True if account includes "CYNGUS"
 */
export function isCyngusAccount(state: RootState): boolean {
  return state.account.includes("CYNGUS");
}

/**
 * Retrieves all content node IDs.
 *
 * @param {RootState} state - Root state
 * @returns {string[]} Array of content node IDs
 */
export function contentNodeNames(state: RootState): string[] {
  return (state.nodes.content && Object.keys(state.nodes.content)) || [];
}

/**
 * Retrieves node changes/snapshots.
 *
 * @param {RootState} state - Root state
 * @returns {string[]} Array of node changes
 */
export function changes(state: RootState): string[] {
  return state.nodes?.changes || {};
}

/**
 * Retrieves release version information.
 *
 * @param {RootState} state - Root state
 * @returns {{ dashboard: string; server: string }} Dashboard and server release IDs
 */
export function releaseId(state: RootState): { dashboard: string; server: string } {
  return state.releaseId;
}

/**
 * Retrieves all trending topics.
 *
 * @param {RootState} state - Root state
 * @returns {TrendingType[]} Array of trending topics
 */
export function trendingTopics(state: RootState): TrendingType[] {
  return state.trendingTopics;
}

/**
 * Retrieves currently published topics.
 *
 * @param {RootState} state - Root state
 * @returns {TrendingType[]} Array of currently published topics
 */
export function currentlyPublished(state: RootState): TrendingType[] {
  return state.currentlyTopics;
}

/**
 * Retrieves previously published topics.
 *
 * @param {RootState} state - Root state
 * @returns {TrendingType[]} Array of previously published topics
 */
export function previousPublished(state: RootState): TrendingType[] {
  return state.previousTopics;
}

/**
 * Retrieves filter date range.
 *
 * @param {RootState} state - Root state
 * @returns {Date[]} Array with start and end dates
 */
export function filterDate(state: RootState): Date[] {
  return state.filterDate;
}

/**
 * Retrieves current topic order updates.
 *
 * @param {RootState} state - Root state
 * @returns {OrderUpdate[]} Array of order updates
 */
export function orderCurrently(state: RootState): OrderUpdate[] {
  return state.orderCurrently;
}

/**
 * Checks if UI beta features are enabled.
 *
 * @param {RootState} state - Root state
 * @returns {Boolean} True if UI beta enabled in webchat module
 */
export function isUiBetaEnabled(state: RootState): Boolean {
  return state.modules.webchat.ui_beta;
}

/**
 * Checks if right-to-left (RTL) mode is enabled.
 *
 * @param {RootState} state - Root state
 * @returns {Boolean} True if RTL mode enabled in core module
 */
export function isRtlModeEnabled(state: RootState): Boolean {
  return state.modules.core.rtlMode;
}

/**
 * Checks if intent testing has been completed.
 *
 * @param {RootState} state - Root state
 * @returns {Boolean} True if intent testing is complete
 */
export function intentTestingCompleted(state: RootState): Boolean {
  return state.training.intentTestingCompleted;
}
